import { FC, ReactNode, CSSProperties } from "react";
import CustomButton from "../button/CustomButton";
import useStyles from "./StandardModalStyles";
import CustomModal from "../modal/CustomModal";
import { FaXmark } from "react-icons/fa6";

interface StandardModalProps {
  showModal: boolean;
  onCloseModal: () => void;
  bannerSrc?: string;
  videoSrc?: string;
  children?: ReactNode;

  containerClassName?: string;
  containerStyle?: CSSProperties;
  customStyleBody?: string;

  headerBackground?: string;
  headerStyle?: CSSProperties;
  headerClassName?: string;
  headerImageClassName?: string;
  headerImageStyle?: CSSProperties;

  closeButtonClassName?: string;
  closeButtonStyle?: CSSProperties;

  topBar?: ReactNode;
  topBarClassName?: string;
  topBarStyle?: CSSProperties;
  topBarTitleClassName?: string;
  topBarTitleStyle?: CSSProperties;
  topBarCloseButtonClassName?: string;
  topBarCloseButtonStyle?: CSSProperties;

  titulo?: ReactNode;
  tituloClassName?: string;
  tituloStyle?: CSSProperties;
  conteudoPrimario?: ReactNode;
  conteudoPrimarioClassName?: string;
  conteudoPrimarioStyle?: CSSProperties;
  conteudoSecundario?: ReactNode;
  conteudoSecundarioClassName?: string;
  conteudoSecundarioStyle?: CSSProperties;

  footer?: ReactNode;
  footerPrimaryLabel?: string;
  footerPrimaryVariant?: "primary" | "outline-primary" | "danger" | "warning" | "danger-alt" | "outline-dark";
  footerPrimarySize?: "sm" | "lg";
  footerPrimaryStyle?: CSSProperties;
  footerPrimaryDisabled?: boolean;
  onFooterPrimaryClick?: () => void;
  footerSecondaryLabel?: string;
  footerSecondaryVariant?: "primary" | "outline-primary" | "danger" | "warning" | "danger-alt" | "outline-dark";
  footerSecondarySize?: "sm" | "lg";
  footerSecondaryStyle?: CSSProperties;
  footerSecondaryDisabled?: boolean;
  onFooterSecondaryClick?: () => void;
  footerContainerStyle?: CSSProperties;
  footerContainerClassName?: string;

  size?: "sm" | "lg" | "xl";
  scrollable?: boolean;
  fecharModalAoClicarFora?: boolean;

  centered?: boolean;
  usarSize?: boolean;
}

const renderMaybeNode = (
  value: ReactNode,
  defaultTag: keyof JSX.IntrinsicElements,
  defaultClassName: string,
  className?: string,
  style?: CSSProperties
) => {
  if (value === undefined || value === null) return null;
  if (typeof value === "object" && value !== null) return value;

  const Tag = defaultTag;
  const combinedClassName = className || defaultClassName;
  return (
    <Tag className={combinedClassName} style={style}>
      {value}
    </Tag>
  );
};

const StandardModal: FC<StandardModalProps> = (props) => {
  const {
    showModal,
    onCloseModal,
    bannerSrc,
    videoSrc,
    children,
    containerClassName,
    containerStyle,
    customStyleBody,
    headerBackground,
    headerStyle,
    headerClassName,
    headerImageClassName,
    headerImageStyle,
    closeButtonClassName,
    closeButtonStyle,
    topBar,
    topBarClassName,
    topBarStyle,
    topBarTitleClassName,
    topBarTitleStyle,
    topBarCloseButtonClassName,
    topBarCloseButtonStyle,
    titulo,
    tituloClassName,
    tituloStyle,
    conteudoPrimario,
    conteudoPrimarioClassName,
    conteudoPrimarioStyle,
    conteudoSecundario,
    conteudoSecundarioClassName,
    conteudoSecundarioStyle,
    footer,
    footerPrimaryLabel,
    footerPrimaryVariant = "primary",
    footerPrimarySize,
    footerPrimaryStyle,
    footerPrimaryDisabled,
    onFooterPrimaryClick,
    footerSecondaryLabel,
    footerSecondaryVariant = "warning",
    footerSecondarySize,
    footerSecondaryStyle,
    footerSecondaryDisabled,
    onFooterSecondaryClick,
    footerContainerStyle,
    footerContainerClassName,
    size = "lg",
    scrollable,
    fecharModalAoClicarFora,
    centered = false,
    usarSize,
  } = props;

  const classes = useStyles();

  const getYouTubeEmbedUrl = (url: string): string | null => {
    if (!url) return null;
    let videoId: string | null = null;
    if (url.includes("youtube.com/watch")) {
      const urlParams = new URLSearchParams(url.split("?")[1]);
      videoId = urlParams.get("v");
    } else if (url.includes("youtu.be/")) {
      const parts = url.split("youtu.be/");
      if (parts[1]) videoId = parts[1].split("?")[0];
    } else if (url.length === 11) {
      videoId = url;
    }
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  const youTubeEmbedUrl = videoSrc ? getYouTubeEmbedUrl(videoSrc) : null;
  const isLocalVideo = videoSrc && !youTubeEmbedUrl;

  const hasTopBar = topBar !== undefined && topBar !== null && topBar !== false;

  const composedHeaderStyle: CSSProperties = {
    position: "relative",
    ...(headerBackground ? { background: headerBackground } : {}),
    ...(headerStyle || {}),
  };

  const customHeaderContent = (
    <div className={headerClassName || classes.standardModalHeader} style={composedHeaderStyle}>
      {hasTopBar && (
        <div className={`${classes.standardTopbar} ${topBarClassName || ""}`} style={topBarStyle}>
          {renderMaybeNode(topBar, "span", classes.standardTopbarTitle, topBarTitleClassName, topBarTitleStyle)}
          <button
            className={topBarCloseButtonClassName || classes.standardTopbarClose}
            style={topBarCloseButtonStyle}
            onClick={onCloseModal}
            aria-label="Fechar"
          >
            <FaXmark />
          </button>
        </div>
      )}
      {youTubeEmbedUrl ? (
        <div className={classes.standardVideoContainer}>
          <iframe
            src={youTubeEmbedUrl}
            title="Video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className={classes.standardVideo}
          />
        </div>
      ) : isLocalVideo ? (
        <div className={classes.standardVideoContainer}>
          <video src={videoSrc} title="Video Local" controls autoPlay loop className={classes.standardVideo} />
        </div>
      ) : (
        bannerSrc && (
          <img
            src={bannerSrc}
            alt="Banner"
            className={headerImageClassName || classes.standardHeaderImage}
            style={headerImageStyle}
          />
        )
      )}
      {!hasTopBar && (
        <div className={classes.containerCloseButton}>
          <button
            className={closeButtonClassName || classes.standardCloseButton}
            style={closeButtonStyle}
            onClick={onCloseModal}
            aria-label="Fechar"
          >
            <FaXmark />
          </button>
        </div>
      )}
    </div>
  );

  const modalBodyContent = (
    <div style={containerStyle}>
      {children !== undefined && children !== null ? (
        children
      ) : (
        <>
          {renderMaybeNode(titulo, "h2", classes.standardHeading, tituloClassName, tituloStyle)}
          {renderMaybeNode(
            conteudoPrimario,
            "div",
            classes.standardSection,
            conteudoPrimarioClassName,
            conteudoPrimarioStyle
          )}
          {renderMaybeNode(
            conteudoSecundario,
            "div",
            classes.standardSection,
            conteudoSecundarioClassName,
            conteudoSecundarioStyle
          )}
        </>
      )}
    </div>
  );

  const customFooterContent = (() => {
    if (footer !== undefined) return footer;
    if (footerPrimaryLabel || footerSecondaryLabel) {
      return (
        <div
          className={footerContainerClassName}
          style={footerContainerStyle || { display: "flex", justifyContent: "flex-end", width: "100%" }}
        >
          {footerSecondaryLabel && onFooterSecondaryClick && (
            <CustomButton
              label={footerSecondaryLabel}
              variant={footerSecondaryVariant}
              onClick={onFooterSecondaryClick}
              size={footerSecondarySize}
              style={{ marginRight: 8, ...footerSecondaryStyle }}
              disabled={footerSecondaryDisabled}
              id="footer-secondary-button"
            />
          )}
          {footerPrimaryLabel && onFooterPrimaryClick && (
            <CustomButton
              label={footerPrimaryLabel}
              variant={footerPrimaryVariant}
              onClick={onFooterPrimaryClick}
              size={footerPrimarySize}
              style={footerPrimaryStyle}
              disabled={footerPrimaryDisabled}
              id="footer-primary-button"
            />
          )}
        </div>
      );
    }
    return undefined;
  })();

  const containerBodyClass = containerStyle ? "" : classes.standardModalContainer;
  const combinedBodyClass = [containerBodyClass, containerClassName, customStyleBody].filter(Boolean).join(" ");
  return (
    <CustomModal
      showModal={showModal}
      onCloseModal={onCloseModal}
      tipoModal="MODAL_CENTRAL"
      size={size}
      scrollable={scrollable}
      fecharModalAoClicarFora={fecharModalAoClicarFora}
      customHeader={customHeaderContent}
      customFooter={customFooterContent}
      bodyClassName={combinedBodyClass}
      centered={centered}
      usarSize={usarSize}
    >
      {modalBodyContent}
    </CustomModal>
  );
};

export default StandardModal;
